import React from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Container,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  useMediaQuery,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Home as HomeIcon,
  History as HistoryIcon,
  Receipt as ReceiptIcon,
  Build as BuildIcon,
  ContactPhone as ContactIcon,
  Description as AgreementIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { usePortalAuth } from '../context/PortalAuthContext';
import { PortalHeader } from './PortalHeader';
import { useTranslation } from 'react-i18next';

const baseNavItems = [
  { label: 'Home', path: '/', icon: <HomeIcon /> },
  { label: 'Services', path: '/service-history', icon: <HistoryIcon /> },
  { label: 'Invoices', path: '/invoices', icon: <ReceiptIcon /> },
  { label: 'Work', path: '/work-orders', icon: <BuildIcon /> },
  { label: 'Contact', path: '/contact', icon: <ContactIcon /> },
];

export const PortalLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompactHeader = useMediaQuery(theme.breakpoints.down('lg'));
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const navigate = useNavigate();
  const location = useLocation();
  const { account, customer, logout } = usePortalAuth();
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const menuOpen = isCompactHeader && Boolean(menuAnchor);

  React.useEffect(() => {
    if (!isCompactHeader) setMenuAnchor(null);
  }, [isCompactHeader]);

  const navItems = React.useMemo(() => {
    const items = [...baseNavItems];
    if (customer?.agreementStatus === 'sent' || customer?.agreementStatus === 'signed') {
      items.splice(4, 0, { label: 'Agreement', path: '/service-agreement', icon: <AgreementIcon /> });
    }
    return items;
  }, [customer?.agreementStatus]);

  const currentNavIndex = navItems.findIndex(item =>
    location.pathname === item.path ||
    (item.path !== '/' && location.pathname.startsWith(item.path))
  );

  const handleNavChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(navItems[newValue].path);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setMenuAnchor(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <PortalHeader
        businessName={account?.businessName}
        logoUrl={account?.logoUrl}
      >
        {isCompactHeader ? (
          <IconButton
            id="portal-navigation-button"
            color="inherit"
            aria-label={t('Open navigation menu')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? 'portal-navigation-menu' : undefined}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ flexShrink: 0, width: 44, height: 44 }}
          >
            <MenuIcon />
          </IconButton>
        ) : (
          <Box component="nav" aria-label={t('Main navigation')} sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            {navItems.map((item, index) => (
              <Button
                key={item.path}
                color="inherit"
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                aria-current={currentNavIndex === index ? 'page' : undefined}
                sx={{
                  px: 1.5,
                  py: 1,
                  whiteSpace: 'nowrap',
                  color: currentNavIndex === index ? 'primary.main' : 'text.secondary',
                  fontWeight: currentNavIndex === index ? 600 : 400,
                }}
              >
                {t(item.label)}
              </Button>
            ))}
          </Box>
        )}
      </PortalHeader>

      {/* Compact Navigation Dropdown */}
      <Menu
        id="portal-navigation-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        transitionDuration={prefersReducedMotion ? 0 : 150}
        slotProps={{
          list: { 'aria-labelledby': 'portal-navigation-button' },
          paper: {
            elevation: 3,
            sx: { mt: 1, width: 232, maxWidth: 'calc(100vw - 32px)', border: 1, borderColor: 'divider', borderRadius: '10px' },
          },
        }}
      >
        {navItems.map((item, index) => (
          <MenuItem
            key={item.path}
            onClick={() => handleMenuItemClick(item.path)}
            selected={currentNavIndex === index}
            aria-current={currentNavIndex === index ? 'page' : undefined}
            sx={{ minHeight: 44 }}
          >
            <ListItemIcon sx={{ color: currentNavIndex === index ? 'primary.main' : 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={t(item.label)} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { setMenuAnchor(null); logout(); }} sx={{ minHeight: 44 }}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={t('Sign Out')} />
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, pb: isMobile ? 8 : 0 }}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Outlet />
        </Container>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper
          sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }}
          elevation={3}
        >
          <BottomNavigation
            value={currentNavIndex >= 0 ? currentNavIndex : 0}
            onChange={handleNavChange}
            showLabels
          >
            {navItems.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={t(item.label)}
                icon={item.icon}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};
